/// <amd-module name="Search/Code/Components/ItemRow" />

import * as React from "react";
import * as Resources from "Search/Code/Resources";
import * as Constants from "Search/Code/Constants";
import { ItemRowContextualMenuButton } from "Search/Code/Components/ItemContextualMenu";
import { css } from "VSSUI/Util";
import { Tooltip } from "VSSUI/TooltipEx";
import { Location } from "VSSUI/Utilities/Position";
import { Link } from "VSSUI/Link";
import { isVCType, isGitType } from "Search/Code/Utils";
import { IMenuItem } from "VSSUI/Menu";
import { CodeResult } from "Search/LegacyWebApi/Contracts/Code";

export interface IItemRowProps {
    active: boolean;

    item: CodeResult;

    previewOrientation: string;

    fileUrl: string | undefined;

    onMenuItemInvoked(menuItem: IMenuItem): void;

    onFileNameClicked(link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void;

    onRenderComplete(): void;
}

export class ItemRow extends React.PureComponent<IItemRowProps> {
    public render(): JSX.Element {
        const { item, previewOrientation, onMenuItemInvoked, onFileNameClicked, fileUrl, active } = this.props;
        const { fileName, project, matches } = item;
        const hitCount = matches[Constants.ContentHitKey].length;
        const hitsMessage = hitCount > 0 && (
            hitCount >= Constants.MaxHitsToHighlight
                ? `${Constants.MaxHitsToHighlight}+ ${Resources.Matches}`
                : `${hitCount} ${(hitCount > 1 ? Resources.Matches : Resources.Match)}`);
        const isCustomVC: boolean = !isVCType(item.vcType);
        const repoNameRequired = isGitType(item.vcType) || isCustomVC;
        const projectRepoName = repoNameRequired ? `${project} > ${item.repository}` : project;

        const cellHeader =
            <span className="file-info flex-grow">
                {
                    active && !isCustomVC ? (
                        <Link className="file-link fontWeightSemiBold fontSizeM"
                            href={fileUrl} target="_blank"
                            aria-label={Resources.FileNameLabel}
                            onClick={evt => fileUrl && onFileNameClicked(fileUrl, evt)}
                            rel="noopener noreferrer"
                        >{fileName}</Link>
                    ) : <span className={css("file-name", isCustomVC && "custom-vc", "fontSizeM", "fontWeightSemiBold")}>{fileName}</span>
                }
                <span className="project-repo-info">{projectRepoName}</span>
            </span>;

        const cellContent =
            <Tooltip
                anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                overflowOnly={true}
                text={item.path}
                tooltipOrigin={{ horizontal: Location.start, vertical: Location.start }}
            >
                <div className="text-content-container flex-grow fontSize">
                    <span>{item.path}</span>
                </div>
            </Tooltip>;

        const horizontalSplitView: JSX.Element =
            <div                
                className={css("code-search-cell flex-grow scroll-hidden", active  && "is-active")}>
                <div className="cell-header flex-row fontSize">
                    {cellHeader}
                    <span className="hits fontSize">{hitsMessage}</span>
                </div>
                <div className="cell-content fontSizeMS flex-row">
                    {cellContent}
                    <ItemRowContextualMenuButton item={item} onMenuItemInvoked={onMenuItemInvoked} itemUrl={fileUrl} />
                </div>
            </div>;

        const verticalSplitView: JSX.Element =
            <div
                className={css("code-search-cell flex-grow vertical-mode scroll-hidden", active  && "is-active")}>
                <div className="cell-header flex-row fontSize">
                    {cellHeader}
                </div>
                <ItemRowContextualMenuButton item={item} onMenuItemInvoked={onMenuItemInvoked} itemUrl={fileUrl} />
                <div className="cell-content fontSizeMS flex-row">
                    {cellContent}
                </div>
                <span className="hits fontSize">{hitsMessage}</span>
            </div>;

        return previewOrientation === Constants.PreviewOrientationActionIds.Bottom ? verticalSplitView : horizontalSplitView;
    }

    public componentDidMount(): void {
        if (this.props.onRenderComplete) {
            this.props.onRenderComplete();
        }
    }

    public componentDidUpdate(): void {
        if (this.props.onRenderComplete) {
            this.props.onRenderComplete();
        }
    }
};